import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, BadgeCheck, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import CallbackModal from "@/components/CallbackModal";
import { useSEO } from "@/lib/seo";

export default function InstituteDetail() {
  const { slug } = useParams();
  const [inst, setInst] = useState(null);
  const [callbackOpen, setCallbackOpen] = useState(false);

  useEffect(() => {
    api.get(`/institutes/${slug}`).then((r) => setInst(r.data)).catch(() => setInst(false));
  }, [slug]);

  useSEO(inst ? {
    title: `${inst.name} Programs — Training Basket`,
    description: inst.description || `Explore programs from ${inst.name}.`,
    canonical: `${window.location.origin}/institutes/${slug}`,
  } : {});

  if (inst === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p className="font-bold text-xl">Institute not found</p><Link to="/institutes" className="text-rose-600 font-semibold text-sm mt-2 inline-block">All institutes</Link></div>;
  if (!inst) return <div className="max-w-7xl mx-auto px-6 py-16"><div className="h-80 rounded-3xl bg-slate-100 animate-pulse" /></div>;

  return (
    <div data-testid="institute-detail-page">
      <section className="bg-gradient-to-br from-slate-900 via-slate-900 to-rose-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <Link to="/institutes" className="text-slate-300 text-sm flex items-center gap-1.5 mb-6 hover:text-white" data-testid="back-to-institutes"><ArrowLeft className="w-4 h-4" /> All institutes</Link>
          <div className="flex flex-wrap items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-white text-slate-900 flex items-center justify-center font-extrabold text-xl shrink-0">{inst.short}</div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{inst.name}</h1>
              <p className="text-slate-300 mt-1.5 flex items-center gap-1.5"><MapPin className="w-4 h-4" />{inst.city}{inst.city && inst.country ? ", " : ""}{inst.country}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            {inst.ranking && <span className="px-4 py-2 rounded-full text-xs font-bold bg-amber-400/15 text-amber-300 border border-amber-400/30">{inst.ranking}</span>}
            {inst.accreditation && <span className="px-4 py-2 rounded-full text-xs font-bold bg-emerald-400/15 text-emerald-300 border border-emerald-400/30 flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" />{inst.accreditation}</span>}
          </div>
          {inst.description && <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed">{inst.description}</p>}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center justify-between mb-7">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Programs from {inst.short}</h2>
          <Button variant="outline" className="rounded-xl font-semibold" onClick={() => setCallbackOpen(true)} data-testid="institute-counselor-cta">
            <PhoneCall className="w-4 h-4 mr-1.5" /> Ask a counselor
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(inst.programs || []).map((p) => <CourseCard key={p.slug} program={p} />)}
        </div>
      </section>

      <CallbackModal open={callbackOpen} onOpenChange={setCallbackOpen} />
    </div>
  );
}
